import { FC } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { StatusBadge } from "@components";
import { cardStat, cn, panelSm, Stack } from "@design-system";

interface SignatureCardProps {
  signatureUrl: string;
  signedBy?: string;
  location: string;
  deliveryComment?: string;
  stopType: string;
}

export const SignatureCard: FC<SignatureCardProps> = ({
  signatureUrl,
  signedBy,
  location,
  deliveryComment,
  stopType,
}) => {
  const { t } = useTranslation("common");
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;

  return (
    <div className={cn(cardStat, "p-4 flex flex-col h-full")}>
      <Stack gap={3} className="h-full">
        {/* Signature Image with Absolute Label */}
        <div className={cn(panelSm, "relative flex-grow bg-surface p-2")}>
          {/* Stop Type Label */}
          {stopType && (
            <div className="absolute top-2 right-2">
              <StatusBadge status={stopType} rounded />
            </div>
          )}
          <img
            src={signatureUrl}
            alt={`${t("signature")} ${signedBy || ""}`}
            className="w-full h-full object-contain"
          />
        </div>

        {/* Location */}
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-brand dark:text-lgb-blue-200 hover:underline mt-2"
        >
          <HiOutlineLocationMarker className="w-4 h-4 text-brand" />
          <span className="text-sm">{location}</span>
        </a>

        {/* Signed By - only show if there's actual data */}
        {signedBy && (
          <p className="text-sm font-medium text-text">
            {t("signed_by")}: {signedBy}
          </p>
        )}

        {/* Comment */}
        {deliveryComment && (
          <div className="mt-auto pt-2">
            <p className="text-sm font-medium text-text">
              {t("comment_header")}
            </p>
            <p className="text-sm text-gray-800 dark:text-gray-300">
              {deliveryComment}
            </p>
          </div>
        )}
      </Stack>
    </div>
  );
};
